/**
 * Site-wide announcement strip for visitors (landing, login, register, etc.).
 */

import { useLocation } from 'react-router-dom';

import { SiteAnnouncementBanner } from '@components/features/dashboard/SiteAnnouncementBanner';
import { EVENTS } from '@constants/telemetryEvents';
import { useAuth } from '@contexts/AuthContext';
import { usePublicSiteAnnouncements } from '@contexts/PublicSiteAnnouncementsContext';
import { useTelemetry } from '@hooks/useTelemetry';

/** Routes where signed-out visitors should see the public announcement strip. */
const PUBLIC_VISITOR_PATHS = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/terms',
  '/privacy',
  '/complete-invitation',
  '/family-registration',
  '/verify-email',
  '/verify-phone',
  '/setup-2fa',
  '/onboarding-complete',
]);

function isPublicVisitorPath(pathname: string): boolean {
  if (PUBLIC_VISITOR_PATHS.has(pathname)) return true;
  if (pathname.startsWith('/tutor/')) return true;
  return false;
}

export function PublicSiteAnnouncementStrip() {
  const { pathname } = useLocation();
  const { user, token } = useAuth();
  const { announcements, dismiss } = usePublicSiteAnnouncements();
  const { track } = useTelemetry();

  const isFullySignedIn = Boolean(token && user?.twoFactorEnabled);
  const onPublicVisitorPage = isPublicVisitorPath(pathname);

  if (!onPublicVisitorPage || isFullySignedIn || announcements.length === 0) {
    return null;
  }

  return (
    <div className="bhg-public-site-announcement-strip" role="region" aria-label="Site announcement">
      {announcements.map((announcement) => (
        <SiteAnnouncementBanner
          key={announcement.announcementId}
          announcement={announcement}
          layout="landing"
          onDismiss={() => {
            dismiss(announcement.announcementId);
            track(EVENTS.LANDING_ANNOUNCEMENT_DISMISSED, {
              announcementId: announcement.announcementId,
            });
          }}
        />
      ))}
    </div>
  );
}
